import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import WarningIcon from '@mui/icons-material/Warning';

import Input from '../shared/Input';
import Drawer from '../shared/Drawer';
import Autocomplete from '../shared/Autocomplete';
import { SelectBox, SelectBoxList } from '../shared/select-box';
import Dropzone from '../shared/dropzone/Dropzone';

import { procedureFlows, defaultLeadDays } from '../../constants/cases';
import allCountries from '../../constants/countries';

const procedureFlowsOptions = Object.values(procedureFlows);
const countriesList = allCountries.map((c) => c.name);

const ProcedureModal = (props) => {
  const {
    mode = 'create',
    open,
    onClose,
    onSubmit,
    loading,
    productsList = [],
    documentsList = [],
    onUpload,
    missingProducts = [],
    initialValues,
  } = props;

  const [view, setView] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [activeCountries, setActiveCountries] = useState( []);
  const [flow, setFlow] = useState(null);
  const [products, setProducts] = useState([]);
  const [leadDays, setLeadDays] = useState(defaultLeadDays);
  const [documents, setDocuments] = useState([]);
  const [search, setSearch] = useState('');
  const [files] = useState([]);

  useEffect(() => {
    if (open && mode === 'update') {
      setName(initialValues?.name || '');
      setDescription(initialValues?.description || '');
      setActiveCountries(initialValues?.active || []);
      setFlow(initialValues?.flow || '');
      setProducts(initialValues?.products || []);
      setLeadDays(initialValues?.leadDays || defaultLeadDays);
      setDocuments(initialValues?.documents ? initialValues?.documents?.map((docId) => documentsList.find((d) => d.id === docId)) : []);
    }
  }, [open, mode, initialValues, productsList, documentsList]);

  const resetValues = () => {
    setName('');
    setDescription('');
    setActiveCountries([]);
    setFlow(null);
    setProducts([]);
    setLeadDays(defaultLeadDays);
    setDocuments([]);
    setView(0);
  };

  const handleSubmit = async () => {
    const formObj = {
      name: name || '',
      description: description || '',
      flow: flow || null,
      active: activeCountries || [],
      products: products || [],
      leadDays: leadDays || defaultLeadDays,
      documents: documents?.map((d) => d.id) || [],
    };
    await onSubmit(formObj);
    resetValues();
  }

  const isDisabled = () => {
    return !name;
  };

  const onProductSelect = (value) => {
    if (value && !products?.find((p) => p.id === value?.id)) {
      setProducts([...products, value]);
    }
  };

  const onProductRemove = (id) => {
    setProducts([...products].filter((p) => p.id !== id));
    setSearch('');
  }

  const productsOptions = useMemo(() => {
    return productsList.filter((p) => !products?.find((product) => product?.id === p?.id));
  }, [productsList, products]);

  return (
    <Drawer
      open={open}
      onClose={() => {
        resetValues();
        onClose();
      }}
      title={mode === 'update' ? 'Edit Procedure' : 'Create Procedure'}
      subtitle="Enter procedure details"
      submitText={mode === 'update' ? 'Save Changes' : 'Create'}
      disabled={isDisabled()}
      loading={loading}
      onSubmit={handleSubmit}
    >
      <Box sx={{ minWidth: { sm: '510px', xs: 'auto' } }}>
        {view === 0 ? (
          <Grid container spacing={1} sx={{ maxWidth: '510px' }}>
            <Grid size={{ xs: 12 }}>
              <Input
                id="name"
                type="text"
                placeholder=""
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                label="Procedure Name"
                required
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Input
                id="description"
                type="text"
                placeholder=""
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                label="Description"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                value={activeCountries}
                onChange={setActiveCountries}
                options={countriesList}
                label="Countries"
                multiple
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                value={flow}
                onChange={setFlow}
                options={procedureFlowsOptions}
                label="Flow"
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Input
                id="leadDays"
                type="number"
                placeholder=""
                value={leadDays}
                onChange={(e) => setLeadDays(e.target.value)}
                fullWidth
                label="Lead Days"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                value={documents}
                onChange={setDocuments}
                getOptionLabel={(option) => option.name}
                options={documentsList}
                label="Documents"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              {/*<Autocomplete*/}
              {/*  multiple*/}
              {/*  value={products}*/}
              {/*  onChange={setProducts}*/}
              {/*  getOptionLabel={(option) => `${option.sku} - ${option.description}`}*/}
              {/*  options={productsList}*/}
              {/*  label="Products"*/}
              {/*/>*/}

              <SelectBox
                label="Products"
                selected={products?.map((p) => ({ key: p?.id, label: p?.sku }))}
                onClick={() => setView(1)}
              />

              <Box sx={{ mt: 2 }}>
                <Dropzone
                  multiple={false}
                  files={files}
                  onUpload={onUpload}
                  type="csv"
                  text="Drag and drop .csv file here or click to upload"
                  hideFilesList
                  loading={loading}
                />
                <div className='m-t-sm'>
                  <span className='font-bold'>Format:</span>
                  <span className='m-l-sm'>Sku, Description</span>
                </div>

                {!!missingProducts?.length && (
                  <Box mt={1}>
                    <Box sx={{ display: 'flex' }}>
                      <WarningIcon color="warning" />
                      <Typography fontSize={16} ml={1} color="warning">Missing products:</Typography>
                    </Box>
                    {missingProducts?.map((product) => (
                      <Box sx={{ mt: 1, display: 'flex' }} key={`${product}_${Math.random()}`}>
                        <Typography fontSize={14}>{product}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        ) : (
          <SelectBoxList
            sx={{ maxWidth: '510px' }}
            title="Select Products"
            onBackClick={() => setView(0)}
            search={search}
            setSearch={setSearch}
            onChange={(selected) => {
              onProductSelect(selected);
              setSearch('');
            }}
            searchLabel="Add Product"
            options={productsOptions}
            getOptionLabel={(option) => `${option?.sku} – ${option.description}`}
            selected={sortBy(products, 'sku')}
            onRemoveItem={onProductRemove}
            renderSelectedItem={(product) => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 14 }}>
                  {product.sku}
                </Typography>
                <Typography sx={{ fontSize: 14, ml: 1, color: '#A4A7AE' }}>
                  {product?.description}
                </Typography>
                {product?.tags?.map((tag) => (
                  <Chip
                    key={tag.id}
                    size="small"
                    label={tag.label}
                    variant="outlined"
                    sx={{ fontWeight: 500, backgroundColor: 'var(--color-main-light)', borderColor: 'var(--color-main-border)', ml: 1 }}
                  />
                ))}
              </Box>
            )}
          />
        )}
      </Box>
    </Drawer>
  );
};

ProcedureModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func
};

export default ProcedureModal;
